import { useMemo, useState } from 'react'
import { useData } from '../context/DataContext'
import { PageLoading } from '../components/common/LoadingSpinner'
import KPICard from '../components/common/KPICard'
import ChartCard from '../components/common/ChartCard'
import FilterDropdown from '../components/common/FilterDropdown'
import DateRangePicker from '../components/common/DateRangePicker'
import LineChart from '../components/charts/LineChart'
import BarChart from '../components/charts/BarChart'
import {
  UserPlusIcon,
  UsersIcon,
  DocumentCheckIcon,
  TrophyIcon,
  ChartBarIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'
import {
  formatCurrency,
  formatPercent,
  formatMonth,
  getCountryName
} from '../utils/formatters'

const STAGES = ['MQL', 'SQL', 'Opportunity', 'Proposal', 'Contract Sent', 'Closed Won']

export default function MarketingSalesOverview() {
  const { data, computed, loading, error } = useData()
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [startMonth, setStartMonth] = useState(null)
  const [endMonth, setEndMonth] = useState(null)

  const filteredData = useMemo(() => {
    if (!data || !computed) return null

    const { months } = computed
    const start = startMonth || months[Math.max(0, months.length - 12)]
    const end = endMonth || months[months.length - 1]

    // Filter deals by date range
    let deals = data.deals.filter(d => {
      const mqlMonth = d.mql_date?.substring(0, 7)
      return mqlMonth && mqlMonth >= start && mqlMonth <= end
    })

    if (selectedCountry) {
      deals = deals.filter(d => d.country === selectedCountry)
    }

    // Count deals by stage
    const stageCounts = STAGES.reduce((acc, stage) => {
      acc[stage] = deals.filter(d => {
        if (stage === 'MQL') return d.mql_date
        if (stage === 'SQL') return d.sql_date
        if (stage === 'Opportunity') return d.opportunity_date
        if (stage === 'Proposal') return d.proposal_date
        if (stage === 'Contract Sent') return d.contract_sent_date
        if (stage === 'Closed Won') return d.closed_won_date
        return false
      }).length
      return acc
    }, {})

    // Closed deals
    const closedWon = deals.filter(d => d.closed_won_date)
    const closedLost = deals.filter(d => d.closed_lost_date)

    const totalClosedWonValue = closedWon.reduce((sum, d) => sum + (d.deal_value || 0), 0)
    const avgClosedWonValue = closedWon.length > 0 ? totalClosedWonValue / closedWon.length : 0
    const winRate = (closedWon.length + closedLost.length) > 0
      ? closedWon.length / (closedWon.length + closedLost.length)
      : 0

    // Monthly closed won trend
    const filteredMonths = months.filter(m => m >= start && m <= end)
    const closedWonByMonth = filteredMonths.map(month => {
      const monthDeals = closedWon.filter(d => d.closed_won_date?.substring(0, 7) === month)
      return monthDeals.reduce((sum, d) => sum + (d.deal_value || 0), 0)
    })

    // Average closed won by month
    const avgClosedWonByMonth = filteredMonths.map(month => {
      const monthDeals = closedWon.filter(d => d.closed_won_date?.substring(0, 7) === month)
      return monthDeals.length > 0
        ? monthDeals.reduce((sum, d) => sum + (d.deal_value || 0), 0) / monthDeals.length
        : 0
    })

    return {
      months: filteredMonths,
      stageCounts,
      closedWon,
      closedLost,
      totalClosedWonValue,
      avgClosedWonValue,
      winRate,
      closedWonByMonth,
      avgClosedWonByMonth
    }
  }, [data, computed, selectedCountry, startMonth, endMonth])

  if (loading) return <PageLoading />
  if (error) return <div className="text-red-500">Error: {error}</div>
  if (!filteredData) return null

  const {
    months,
    stageCounts,
    closedWon,
    closedLost,
    totalClosedWonValue,
    avgClosedWonValue,
    winRate,
    closedWonByMonth,
    avgClosedWonByMonth
  } = filteredData

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <FilterDropdown
          label="Country"
          value={selectedCountry}
          options={computed.countries.map(c => ({ value: c, label: getCountryName(c) }))}
          onChange={setSelectedCountry}
          placeholder="All Countries"
        />
        <DateRangePicker
          startMonth={startMonth}
          endMonth={endMonth}
          months={computed.months}
          onStartChange={setStartMonth}
          onEndChange={setEndMonth}
        />
      </div>

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard
          title="MQLs"
          value={stageCounts['MQL']?.toLocaleString()}
          icon={UserPlusIcon}
        />
        <KPICard
          title="SQLs"
          value={stageCounts['SQL']?.toLocaleString()}
          icon={UsersIcon}
        />
        <KPICard
          title="Opportunities"
          value={stageCounts['Opportunity']?.toLocaleString()}
          icon={ChartBarIcon}
        />
        <KPICard
          title="Proposals"
          value={stageCounts['Proposal']?.toLocaleString()}
          icon={DocumentCheckIcon}
        />
        <KPICard
          title="Contracts Sent"
          value={stageCounts['Contract Sent']?.toLocaleString()}
          icon={DocumentCheckIcon}
        />
        <KPICard
          title="Closed Won"
          value={closedWon.length.toLocaleString()}
          icon={TrophyIcon}
          colorScheme="success"
        />
      </div>

      {/* KPI Cards - Row 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPICard
          title="Closed Won Value"
          value={formatCurrency(totalClosedWonValue)}
          icon={CurrencyDollarIcon}
          colorScheme="success"
        />
        <KPICard
          title="Avg Closed Won"
          value={formatCurrency(avgClosedWonValue)}
          icon={CurrencyDollarIcon}
        />
        <KPICard
          title="Win Rate"
          value={formatPercent(winRate)}
          icon={TrophyIcon}
          colorScheme={winRate >= 0.3 ? 'success' : 'warning'}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Average Closed Won" subtitle="Monthly average deal value" height="h-64">
          <LineChart
            labels={months}
            datasets={[{
              label: 'Avg Closed Won',
              data: avgClosedWonByMonth,
              color: '#3b82f6'
            }]}
          />
        </ChartCard>

        <ChartCard title="Total Closed Won" subtitle="Monthly closed revenue" height="h-64">
          <LineChart
            labels={months}
            datasets={[{
              label: 'Total Closed Won',
              data: closedWonByMonth,
              color: '#22c55e'
            }]}
            fill={true}
          />
        </ChartCard>
      </div>

      {/* Funnel Chart */}
      <ChartCard title="Sales Funnel" subtitle="Conversion through stages" height="h-64">
        <BarChart
          labels={STAGES}
          datasets={[{
            label: 'Deals',
            data: STAGES.map(s => stageCounts[s] || 0),
            color: '#6366f1'
          }]}
          formatLabels={false}
          yAxisPrefix=""
        />
      </ChartCard>
    </div>
  )
}
